import { useEffect, useRef, useState } from 'react';
import { cn } from '../cn';

// DESIGN.md §5 hud-readout: monospace signal-green numerals on panel-elev.
// Diegetic chrome — the "criminal terminal" voice. Used for stats, IDs,
// timestamps, money, breach timers. The flash variant inverts to a solid
// signal background for 180ms when `flashKey` changes, then settles. The
// flash is decorative; respects prefers-reduced-motion and skips entirely
// when the OS user opts out.

const HUD_FLASH_MS = 180;

export type HudTone = 'signal' | 'danger' | 'quiet';

export interface HudReadoutProps {
  /** The value to render (commonly a number or short token). */
  children: React.ReactNode;
  /** Visual tone. `signal` (default) is in-character green, `danger` is arterial red, `quiet` is muted ink. */
  tone?: HudTone;
  /**
   * When this value changes, the readout briefly inverts. Stable values
   * suppress the flash. Pass the post-update sentinel (e.g. timestamp,
   * delta count) so re-renders that don't change the value don't fire.
   * `undefined` is accepted as "no flash" (under exactOptionalPropertyTypes).
   */
  flashKey?: string | number | undefined;
  className?: string;
}

const toneStyles: Record<HudTone, { idle: string; flash: string }> = {
  signal: {
    idle: 'bg-panel-elev text-primary',
    flash: 'bg-primary text-primary-foreground',
  },
  danger: {
    idle: 'bg-destructive-soft/40 text-destructive-strong',
    flash: 'bg-destructive text-destructive-foreground',
  },
  quiet: {
    idle: 'bg-panel text-ink-mid',
    flash: 'bg-ink-mid text-background',
  },
};

export function HudReadout({
  children,
  tone = 'signal',
  flashKey,
  className,
}: HudReadoutProps) {
  const [flashing, setFlashing] = useState(false);
  const lastKey = useRef<string | number | undefined>(flashKey);

  useEffect(() => {
    if (flashKey === undefined) return;
    if (lastKey.current === flashKey) return;
    lastKey.current = flashKey;
    if (typeof window === 'undefined') return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    setFlashing(true);
    const t = window.setTimeout(() => setFlashing(false), HUD_FLASH_MS);
    return () => window.clearTimeout(t);
  }, [flashKey]);

  const styles = toneStyles[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-xs px-2 py-0.5 font-mono text-[13px] leading-[1.4] tabular-nums',
        flashing ? styles.flash : styles.idle,
        className,
      )}
    >
      {children}
    </span>
  );
}
